import React, { useState } from 'react'
import { Box, Typography, Button, styled, Switch, Divider } from '@mui/material'
import { Lock, Logout, Shield, Devices } from '@mui/icons-material'
import { useAuth } from '@/hooks/common'
import ChangePasswordModal from '../../ChangePasswordModal'

// ==================== STYLED COMPONENTS ====================
const SectionHeader = styled(Box)({
  marginBottom: '32px',
})

const SectionTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  letterSpacing: '-0.02em',
  marginBottom: '8px',
})

const SectionDescription = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#6b7280',
  lineHeight: 1.6,
})

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  marginBottom: '16px',
})

const CardHeader = styled(Box)({
  padding: '20px 24px',
  borderBottom: '1px solid #e5e7eb',
})

const CardContent = styled(Box)({
  padding: '24px',
})

const SettingRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 0',

  '&:not(:last-child)': {
    borderBottom: '1px solid #f3f4f6',
  },
})

const IconBox = styled(Box)<{ color: string }>(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: '10px',
  backgroundColor: `${color}15`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& .MuiSvgIcon-root': {
    fontSize: '20px',
    color,
  },
}))

// ==================== COMPONENT ====================
const AccountSection: React.FC = () => {
  const { logout } = useAuth()
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)

  const handleLogout = () => {
    logout()
  }

  return (
    <>
      <SectionHeader>
        <SectionTitle>Account</SectionTitle>
        <SectionDescription>Manage your account security and preferences</SectionDescription>
      </SectionHeader>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Security
          </Typography>
        </CardHeader>
        <CardContent>
          <SettingRow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconBox color='#3b82f6'>
                <Lock />
              </IconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  Password
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#6b7280',
                  }}
                >
                  Change your password regularly
                </Typography>
              </Box>
            </Box>
            <Button
              variant='outlined'
              size='small'
              onClick={() => setChangePasswordOpen(true)}
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#d1d5db',
                color: '#4b5563',
                '&:hover': {
                  borderColor: '#9ca3af',
                  backgroundColor: '#f9fafb',
                },
              }}
            >
              Change Password
            </Button>
          </SettingRow>

          <SettingRow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconBox color='#10b981'>
                <Shield />
              </IconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  Two-Factor Authentication
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#6b7280',
                  }}
                >
                  Add an extra layer of security
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={twoFactorEnabled}
              onChange={(e) => setTwoFactorEnabled(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#10b981',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#10b981',
                },
              }}
            />
          </SettingRow>
        </CardContent>
      </Card>

      {/* Sessions */}
      <Card>
        <CardHeader>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
            }}
          >
            Active Sessions
          </Typography>
        </CardHeader>
        <CardContent>
          <SettingRow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconBox color='#8b5cf6'>
                <Devices />
              </IconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  Current Session
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#6b7280',
                  }}
                >
                  {navigator.userAgent.includes('Mac') ? 'MacOS' : 'Windows'} •{' '}
                  {navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Browser'}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#10b981',
                px: 1.5,
                py: 0.5,
                backgroundColor: '#d1fae5',
                borderRadius: '6px',
              }}
            >
              ACTIVE
            </Typography>
          </SettingRow>
        </CardContent>
      </Card>

      {/* Sign Out */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconBox color='#ef4444'>
                <Logout />
              </IconBox>
              <Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#111827',
                    mb: 0.5,
                  }}
                >
                  Sign Out
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#6b7280',
                  }}
                >
                  Sign out of your account on this device
                </Typography>
              </Box>
            </Box>
            <Button
              variant='outlined'
              size='small'
              onClick={handleLogout}
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#fca5a5',
                color: '#ef4444',
                '&:hover': {
                  borderColor: '#ef4444',
                  backgroundColor: '#fef2f2',
                },
              }}
            >
              Sign Out
            </Button>
          </Box>
        </CardContent>
      </Card>

      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </>
  )
}

export default AccountSection
